import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PublicLayout from '../layouts/PublicLayout';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { getPropertyDetail, getPropertyAvailability, createBooking, getPolicyVersions, getPropertyReviews } from '../lib/api';
import { format, differenceInDays, addMonths, eachDayOfInterval, getDay } from 'date-fns';
import { toast } from 'sonner';
import { ReviewCard, ReviewStats } from '../components/ReviewCard';
import {
  MapPin,
  Bed,
  Bath,
  Users,
  CalendarDays,
  Zap,
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  Star,
  Tag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AMENITY_ICONS = {
  pool: '🏊',
  waterfront: '🌊',
  pet_friendly: '🐾',
  wifi: '📶',
  air_conditioning: '❄️',
  kitchen: '🍳',
  washer_dryer: '🧺',
  parking: '🅿️',
  beach_access: '🏖️',
  boat_dock: '⛵',
  hot_tub: '♨️',
  bbq_grill: '🔥',
};

const AMENITY_LABELS = {
  pool: 'Pool',
  waterfront: 'Waterfront',
  pet_friendly: 'Pet Friendly',
  wifi: 'WiFi',
  air_conditioning: 'Air Conditioning',
  kitchen: 'Full Kitchen',
  washer_dryer: 'Washer/Dryer',
  parking: 'Free Parking',
  beach_access: 'Beach Access',
  boat_dock: 'Boat Dock',
  hot_tub: 'Hot Tub',
  bbq_grill: 'BBQ Grill',
};

export default function ListingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unavailableDates, setUnavailableDates] = useState([]);
  
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState(1);
  
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  
  const [booking, setBooking] = useState(false);
  const [acceptCheckoutTerms, setAcceptCheckoutTerms] = useState(false);
  const [policyVersions, setPolicyVersions] = useState({});
  
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsCursor, setReviewsCursor] = useState(null);
  const [hasMoreReviews, setHasMoreReviews] = useState(false);

  // Extract property ID from slug (format: slug-uuid where uuid has format xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
  const extractPropertyId = (slug) => {
    if (!slug) return null;
    // UUID regex pattern
    const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    const match = slug.match(uuidPattern);
    return match ? match[0] : slug;
  };
  const propertyId = extractPropertyId(slug);

  useEffect(() => {
    const loadProperty = async () => {
      try {
        const response = await getPropertyDetail(propertyId);
        setProperty(response.data);
        
        // Load availability for next 6 months
        const from = format(new Date(), 'yyyy-MM-dd');
        const to = format(addMonths(new Date(), 6), 'yyyy-MM-dd');
        
        const availResponse = await getPropertyAvailability(propertyId, from, to);
        setUnavailableDates(availResponse.data.unavailable_dates || []);
        
        // Load reviews
        loadReviews();
      } catch (error) {
        console.error('Failed to load property:', error);
        toast.error('Property not found');
      } finally {
        setLoading(false);
      }
    };

    if (propertyId) {
      loadProperty();
    }
  }, [propertyId]);
  
  // Load reviews function
  const loadReviews = async (cursor = null) => {
    if (!propertyId) return;
    setReviewsLoading(true);
    try {
      const response = await getPropertyReviews(propertyId, { cursor, limit: 5 });
      if (cursor) {
        setReviews(prev => [...prev, ...response.data.items]);
      } else {
        setReviews(response.data.items);
        setReviewStats(response.data.stats);
      }
      setReviewsCursor(response.data.next_cursor);
      setHasMoreReviews(response.data.has_more);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  // Load policy versions
  useEffect(() => {
    const loadPolicyVersions = async () => {
      try {
        const res = await getPolicyVersions();
        setPolicyVersions(res.data);
      } catch (error) {
        console.error('Failed to load policy versions:', error);
      }
    };
    loadPolicyVersions();
  }, []);

  const formatPrice = (cents) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  // Calculate weekend vs weekday nights
  const calculateNightBreakdown = (startDate, endDate) => {
    if (!startDate || !endDate) return { weekdayNights: 0, weekendNights: 0 };
    
    const days = eachDayOfInterval({ start: startDate, end: new Date(endDate.getTime() - 1) }); // Exclude checkout day
    let weekdayNights = 0;
    let weekendNights = 0;
    
    days.forEach(day => {
      const dayOfWeek = getDay(day);
      // Friday (5) and Saturday (6) are weekend nights
      if (dayOfWeek === 5 || dayOfWeek === 6) {
        weekendNights++;
      } else {
        weekdayNights++;
      }
    });
    
    return { weekdayNights, weekendNights };
  };

  // Determine which discount applies (if any)
  const getApplicableDiscount = (totalNights) => {
    if (!property) return null;
    
    // Monthly discount takes precedence (28+ nights)
    if (totalNights >= 28 && property.monthly_discount_enabled && property.monthly_discount_percent > 0) {
      return {
        type: 'monthly',
        label: 'Monthly discount (28+ nights)',
        percent: property.monthly_discount_percent
      };
    }
    
    // Weekly discount (7+ nights)
    if (totalNights >= 7 && property.weekly_discount_enabled && property.weekly_discount_percent > 0) {
      return {
        type: 'weekly',
        label: 'Weekly discount (7+ nights)',
        percent: property.weekly_discount_percent
      };
    }
    
    return null;
  };

  const calculateTotal = () => {
    if (!checkIn || !checkOut || !property) return null;
    
    const nights = differenceInDays(checkOut, checkIn);
    if (nights <= 0) return null;
    
    const { weekdayNights, weekendNights } = calculateNightBreakdown(checkIn, checkOut);
    
    // Calculate nightly totals
    const weekdayRate = property.base_price_nightly;
    const weekendRate = property.weekend_price_nightly || property.base_price_nightly;
    
    const weekdayTotal = weekdayRate * weekdayNights;
    const weekendTotal = weekendRate * weekendNights;
    const nightlyTotal = weekdayTotal + weekendTotal;
    
    // Calculate discount
    const discount = getApplicableDiscount(nights);
    const discountAmount = discount ? Math.round(nightlyTotal * (discount.percent / 100)) : 0;
    const discountedNightlyTotal = nightlyTotal - discountAmount;
    
    // Add cleaning fee
    const cleaningFee = property.cleaning_fee || 0;
    const subtotal = discountedNightlyTotal + cleaningFee;
    
    // Service fee (6%)
    const serviceFee = Math.round(subtotal * 0.06);
    const total = subtotal + serviceFee;
    
    return {
      nights,
      weekdayNights,
      weekendNights,
      weekdayRate,
      weekendRate,
      weekdayTotal,
      weekendTotal,
      nightlyTotal,
      discount,
      discountAmount,
      discountedNightlyTotal,
      cleaningFee,
      serviceFee,
      total
    };
  };

  const handleBook = async () => {
    if (!isAuthenticated) {
      toast.info('Please log in to book this property');
      navigate('/login', { state: { from: `/listing/${slug}` } });
      return;
    }

    if (!checkIn || !checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    const nights = differenceInDays(checkOut, checkIn);
    if (nights < property.min_nights) {
      toast.error(`Minimum ${property.min_nights} nights required`);
      return;
    }

    if (nights > property.max_nights) {
      toast.error(`Maximum ${property.max_nights} nights allowed`);
      return;
    }

    if (guests > property.max_guests) {
      toast.error(`Maximum ${property.max_guests} guests allowed`);
      return;
    }

    if (!acceptCheckoutTerms) {
      toast.error('You must accept the Terms, Cancellation Policy, and Payments Disclosure to book');
      return;
    }

    // Build accepted policies for checkout
    const acceptedPolicies = [
      { policy_type: 'terms', policy_version: policyVersions.terms || '1.0', context: 'checkout' },
      { policy_type: 'cancellation', policy_version: policyVersions.cancellation || '1.0', context: 'checkout' },
      { policy_type: 'payments', policy_version: policyVersions.payments || '1.0', context: 'checkout' },
      { policy_type: 'guest_responsibilities', policy_version: policyVersions.guest_responsibilities || '1.0', context: 'checkout' }
    ];

    setBooking(true);
    try {
      const response = await createBooking({
        property_id: propertyId,
        check_in: format(checkIn, 'yyyy-MM-dd'),
        check_out: format(checkOut, 'yyyy-MM-dd'),
        guests_count: guests,
        accepted_policies: acceptedPolicies
      });

      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        // Request to book - redirect to booking page
        toast.success('Booking request sent! The host will review your request.');
        navigate(`/app/bookings/${response.data.booking_id}`);
      }
    } catch (error) {
      console.error('Booking failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setBooking(false);
    }
  };

  const isDateUnavailable = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return unavailableDates.includes(dateStr) || date < new Date();
  };

  const getImageUrl = (image, size = 'large') => {
    const path = size === 'thumb' ? image.path_thumb : 
                 size === 'medium' ? image.path_medium : image.path_large;
    if (!path) return 'https://images.unsplash.com/photo-1753194931548-065891743746?w=800&h=600&fit=crop';
    return path.startsWith('http') ? path : `${API_URL}${path}`;
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </PublicLayout>
    );
  }

  if (!property) {
    return (
      <PublicLayout>
        <div className="container-main section-padding text-center">
          <h1 className="font-heading text-2xl font-bold mb-4">Property Not Found</h1>
          <Button onClick={() => navigate('/search')} className="btn-primary">
            Browse Properties
          </Button>
        </div>
      </PublicLayout>
    );
  }

  const pricing = calculateTotal();
  const images = property.images || [];
  const coverImage = images.find(i => i.is_cover) || images[0];
  const seoImage = coverImage ? `${API_URL}${coverImage.url}` : undefined;
  const seoDescription = `${property.bedrooms}BR, ${property.bathrooms}BA vacation rental in ${property.city_name || 'Southwest Florida'}. ${property.description?.substring(0, 120) || ''}...`;

  return (
    <PublicLayout>
      <SEO
        title={`${property.title} | ${property.city_name || 'SWFL'}`}
        description={seoDescription}
        image={seoImage}
        url={`/listing/${slug}`}
        type="article"
      />
      {/* Image Gallery */}
      <section className="container-main pt-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 rounded-xl overflow-hidden">
          {/* Main Image */}
          <div 
            className="md:col-span-2 md:row-span-2 aspect-property md:aspect-auto cursor-pointer"
            onClick={() => { setGalleryIndex(0); setGalleryOpen(true); }}
          >
            <img 
              src={coverImage ? getImageUrl(coverImage) : 'https://images.unsplash.com/photo-1753194931548-065891743746?w=1200&h=900&fit=crop'}
              alt={property.title}
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* Secondary Images */}
          {images.slice(1, 5).map((image, index) => (
            <div 
              key={image.id}
              className="hidden md:block aspect-property cursor-pointer relative"
              onClick={() => { setGalleryIndex(index + 1); setGalleryOpen(true); }}
            >
              <img 
                src={getImageUrl(image, 'medium')}
                alt={`${property.title} ${index + 2}`}
                className="w-full h-full object-cover"
              />
              {index === 3 && images.length > 5 && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="text-white font-medium">+{images.length - 5} more</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {images.length > 1 && (
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => setGalleryOpen(true)}
          >
            View All {images.length} Photos
          </Button>
        )}
      </section>

      {/* Content */}
      <section className="container-main py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div>
              <div className="flex items-center gap-2 text-slate-500 mb-2">
                <MapPin className="w-4 h-4" />
                <span>{property.city_name}</span>
              </div>
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-4">
                {property.title}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-slate-600">
                <span className="flex items-center gap-1">
                  <Bed className="w-5 h-5" />
                  {property.beds} bed{property.beds !== 1 ? 's' : ''}
                </span>
                <span className="flex items-center gap-1">
                  <Bath className="w-5 h-5" />
                  {property.baths} bath{property.baths !== 1 ? 's' : ''}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-5 h-5" />
                  Up to {property.max_guests} guests
                </span>
                {property.instant_book_enabled && (
                  <span className="badge-instant flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    Instant Book
                  </span>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <h2 className="font-heading text-xl font-semibold mb-4">About this property</h2>
              <p className="text-slate-600 whitespace-pre-line">{property.description}</p>
            </div>

            {/* Amenities */}
            <div>
              <h2 className="font-heading text-xl font-semibold mb-4">Amenities</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {property.amenities?.map((amenity) => (
                  <div key={amenity} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <span className="text-xl">{AMENITY_ICONS[amenity] || '✓'}</span>
                    <span className="text-slate-700">{AMENITY_LABELS[amenity] || amenity}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* House Rules */}
            <div>
              <h2 className="font-heading text-xl font-semibold mb-4">House Rules</h2>
              <div className="space-y-2 text-slate-600">
                <p className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" />
                  Check-in: 3:00 PM
                </p>
                <p className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" />
                  Check-out: 11:00 AM
                </p>
                <p className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" />
                  Minimum stay: {property.min_nights} night{property.min_nights !== 1 ? 's' : ''}
                </p>
                <p className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-600" />
                  Maximum guests: {property.max_guests}
                </p>
              </div>
            </div>

            {/* Cancellation Policy */}
            <div>
              <h2 className="font-heading text-xl font-semibold mb-4">Cancellation Policy</h2>
              <p className="text-slate-600 capitalize">
                {property.cancellation_policy === 'flexible' && 'Free cancellation up to 24 hours before check-in.'}
                {property.cancellation_policy === 'moderate' && 'Free cancellation up to 5 days before check-in. 50% refund up to 24 hours before.'}
                {property.cancellation_policy === 'strict' && 'Full refund only if cancelled within 48 hours of booking and at least 14 days before check-in.'}
              </p>
            </div>

            {/* Safety Disclosures */}
            {(property.has_security_cameras || property.has_noise_monitors) && (
              <div>
                <h2 className="font-heading text-xl font-semibold mb-4">Safety & Privacy</h2>
                <div className="space-y-3">
                  {property.has_security_cameras && (
                    <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-amber-600 text-lg">📹</span>
                      </div>
                      <div>
                        <p className="font-medium text-amber-900">Exterior Security Cameras</p>
                        <p className="text-sm text-amber-700">
                          This property has security cameras on the exterior (e.g., driveway, entrance areas).
                        </p>
                      </div>
                    </div>
                  )}
                  {property.has_noise_monitors && (
                    <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-amber-600 text-lg">🔊</span>
                      </div>
                      <div>
                        <p className="font-medium text-amber-900">Noise Monitoring Device</p>
                        <p className="text-sm text-amber-700">
                          This property has a noise monitoring device that detects excessive noise levels (no audio recording).
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Discounts Info */}
            {(property.weekly_discount_enabled || property.monthly_discount_enabled) && (
              <div>
                <h2 className="font-heading text-xl font-semibold mb-4">Length-of-Stay Discounts</h2>
                <div className="space-y-2 text-slate-600">
                  {property.weekly_discount_enabled && property.weekly_discount_percent > 0 && (
                    <p className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      {property.weekly_discount_percent}% off for 7+ nights
                    </p>
                  )}
                  {property.monthly_discount_enabled && property.monthly_discount_percent > 0 && (
                    <p className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-600" />
                      {property.monthly_discount_percent}% off for 28+ nights
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Reviews Section */}
            <div id="reviews" data-testid="reviews-section">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                  Reviews
                </h2>
                {reviewStats && <ReviewStats stats={reviewStats} />}
              </div>
              
              {reviews.length === 0 ? (
                <p className="text-slate-500 text-center py-8">
                  No reviews yet. Be the first to leave a review after your stay!
                </p>
              ) : (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <ReviewCard key={review.id} review={review} />
                  ))}
                  
                  {hasMoreReviews && (
                    <div className="text-center pt-4">
                      <Button
                        variant="outline"
                        onClick={() => loadReviews(reviewsCursor)}
                        disabled={reviewsLoading}
                      >
                        {reviewsLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        Load More Reviews
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Booking Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-24 shadow-sm">
              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-slate-900">
                    {formatPrice(property.base_price_nightly)}
                  </span>
                  <span className="text-slate-500">/ night</span>
                </div>
                {property.weekend_price_nightly && property.weekend_price_nightly !== property.base_price_nightly && (
                  <p className="text-sm text-slate-500 mt-1">
                    {formatPrice(property.weekend_price_nightly)} / night on Fri & Sat
                  </p>
                )}
              </div>

              {/* Date Selection */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">CHECK-IN</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal h-12"
                        data-testid="listing-checkin"
                      >
                        <CalendarDays className="w-4 h-4 mr-2" />
                        {checkIn ? format(checkIn, 'MMM d') : 'Add date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={checkIn}
                        onSelect={setCheckIn}
                        disabled={isDateUnavailable}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">CHECK-OUT</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal h-12"
                        data-testid="listing-checkout"
                      >
                        <CalendarDays className="w-4 h-4 mr-2" />
                        {checkOut ? format(checkOut, 'MMM d') : 'Add date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={checkOut}
                        onSelect={setCheckOut}
                        disabled={(date) => isDateUnavailable(date) || (checkIn && date <= checkIn)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Guests */}
              <div className="mb-4">
                <Label className="text-xs text-slate-500">GUESTS</Label>
                <Input
                  type="number"
                  min="1"
                  max={property.max_guests}
                  value={guests}
                  onChange={(e) => setGuests(Math.min(parseInt(e.target.value) || 1, property.max_guests))}
                  className="h-12"
                  data-testid="listing-guests"
                />
              </div>

              {/* Price Breakdown */}
              {pricing && (
                <div className="space-y-2 py-4 border-t border-slate-100" data-testid="price-breakdown">
                  {/* Weekday nights */}
                  {pricing.weekdayNights > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span>{formatPrice(pricing.weekdayRate)} × {pricing.weekdayNights} {pricing.weekdayNights === 1 ? 'weekday' : 'weekdays'}</span>
                      <span>{formatPrice(pricing.weekdayTotal)}</span>
                    </div>
                  )}
                  {/* Weekend nights */}
                  {pricing.weekendNights > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span>{formatPrice(pricing.weekendRate)} × {pricing.weekendNights} {pricing.weekendNights === 1 ? 'weekend' : 'weekends'}</span>
                      <span>{formatPrice(pricing.weekendTotal)}</span>
                    </div>
                  )}
                  {/* Discount */}
                  {pricing.discount && pricing.discountAmount > 0 && (
                    <div className="flex justify-between text-green-600" data-testid="price-discount">
                      <span className="flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        {pricing.discount.label}
                      </span>
                      <span>-{formatPrice(pricing.discountAmount)}</span>
                    </div>
                  )}
                  {/* Cleaning fee */}
                  {pricing.cleaningFee > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span>Cleaning fee</span>
                      <span>{formatPrice(pricing.cleaningFee)}</span>
                    </div>
                  )}
                  {/* Service fee */}
                  <div className="flex justify-between text-slate-600">
                    <span>Service fee</span>
                    <span>{formatPrice(pricing.serviceFee)}</span>
                  </div>
                  {/* Total */}
                  <div className="flex justify-between font-semibold text-slate-900 pt-2 border-t border-slate-100">
                    <span>Total</span>
                    <span data-testid="price-total">{formatPrice(pricing.total)}</span>
                  </div>
                  {/* Savings callout */}
                  {pricing.discount && pricing.discountAmount > 0 && (
                    <p className="text-xs text-green-600 text-center mt-1">
                      You&apos;re saving {formatPrice(pricing.discountAmount)} with the {pricing.discount.type} discount!
                    </p>
                  )}
                </div>
              )}

              {/* Checkout Policy Acceptance */}
              <div className="flex items-start space-x-3 mt-4 pt-4 border-t border-slate-100">
                <Checkbox
                  id="acceptCheckoutTerms"
                  checked={acceptCheckoutTerms}
                  onCheckedChange={setAcceptCheckoutTerms}
                  data-testid="checkout-accept-terms"
                />
                <Label 
                  htmlFor="acceptCheckoutTerms" 
                  className="text-xs text-slate-500 leading-relaxed cursor-pointer"
                >
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" className="text-primary hover:underline">
                    Terms
                  </Link>,{' '}
                  <Link to="/cancellation" target="_blank" className="text-primary hover:underline">
                    Cancellation Policy
                  </Link>, and{' '}
                  <Link to="/payments" target="_blank" className="text-primary hover:underline">
                    Payments Disclosure
                  </Link>
                </Label>
              </div>

              {/* Book Button */}
              <Button 
                className="w-full btn-accent h-12 mt-4"
                onClick={handleBook}
                disabled={booking || !acceptCheckoutTerms}
                data-testid="book-now-btn"
              >
                {booking ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : property.instant_book_enabled ? (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Book Now
                  </>
                ) : (
                  'Request to Book'
                )}
              </Button>

              <p className="text-center text-xs text-slate-500 mt-4">
                You won&apos;t be charged yet
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery Modal */}
      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="max-w-5xl h-[80vh] p-0">
          <DialogHeader className="absolute top-4 left-4 right-4 z-10">
            <DialogTitle className="sr-only">Image Gallery</DialogTitle>
            <Button 
              variant="ghost" 
              size="icon" 
              className="absolute right-0 top-0 bg-white/80 hover:bg-white"
              onClick={() => setGalleryOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </DialogHeader>
          
          <div className="relative h-full flex items-center justify-center bg-black">
            {images.length > 0 && (
              <>
                <img 
                  src={getImageUrl(images[galleryIndex])}
                  alt={`${property.title} ${galleryIndex + 1}`}
                  className="max-h-full max-w-full object-contain"
                />
                
                {images.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute left-4 bg-white/80 hover:bg-white"
                      onClick={() => setGalleryIndex((galleryIndex - 1 + images.length) % images.length)}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-4 bg-white/80 hover:bg-white"
                      onClick={() => setGalleryIndex((galleryIndex + 1) % images.length)}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                    
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm">
                      {galleryIndex + 1} / {images.length}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </PublicLayout>
  );
}
